import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DevicesTab = () => {
  const [devices] = useState([
    {
      id: 1,
      name: "MacBook Pro 16-inch",
      type: "desktop",
      os: "macOS 14.1",
      browser: "Chrome 119.0",
      location: "San Francisco, CA",
      lastAccess: "2025-09-03T14:30:00Z",
      ipAddress: "192.168.1.100",
      fingerprint: "SHA256:nThbg6kXUpJWGl7E1IGOCspRomTxdCARLviKw6E5SY8",
      isCurrent: true,
      trusted: true
    },
    {
      id: 2,
      name: "iPhone 15 Pro",
      type: "mobile",
      os: "iOS 17.1",
      browser: "Safari Mobile",
      location: "San Francisco, CA",
      lastAccess: "2025-09-03T12:15:00Z",
      ipAddress: "192.168.1.101",
      fingerprint: "SHA256:kXUpJWGl7E1IGOCspRomTxdCARLviKw6E5SY8nThbg6",
      isCurrent: false,
      trusted: true
    },
    {
      id: 3,
      name: "Windows Desktop",
      type: "desktop",
      os: "Windows 11",
      browser: "Edge 119.0",
      location: "New York, NY",
      lastAccess: "2025-09-02T09:45:00Z",
      ipAddress: "203.0.113.45",
      fingerprint: "SHA256:E1IGOCspRomTxdCARLviKw6E5SY8nThbg6kXUpJWGl7",
      isCurrent: false,
      trusted: false
    }
  ]);

  const [showFingerprints, setShowFingerprints] = useState({});

  const formatLastAccess = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return 'Smartphone';
      case 'tablet': return 'Tablet';
      default: return 'Monitor';
    }
  };

  const handleRevokeDevice = (deviceId) => {
    console.log('Revoking device:', deviceId);
    // In a real app, this would revoke the device and update the list
  };

  const handleTrustDevice = (deviceId) => {
    console.log('Trusting device:', deviceId);
    // In a real app, this would mark the device as trusted
  };

  const toggleFingerprint = (deviceId) => {
    setShowFingerprints(prev => ({
      ...prev,
      [deviceId]: !prev?.[deviceId]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Connected Devices</h3>
          <p className="text-sm text-muted-foreground">
            Manage devices that have access to your SecureVault account
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
          Refresh
        </Button>
      </div>
      {/* Device List */}
      <div className="space-y-4">
        {devices?.map((device) => (
          <div key={device?.id} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  device?.trusted ? 'bg-success/10' : 'bg-warning/10'
                }`}>
                  <Icon 
                    name={getDeviceIcon(device?.type)} 
                    size={24} 
                    className={device?.trusted ? 'text-success' : 'text-warning'} 
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-foreground">{device?.name}</h4>
                    {device?.isCurrent && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                        Current Device
                      </span>
                    )}
                    {device?.trusted && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                        <Icon name="Shield" size={12} className="mr-1" />
                        Trusted
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>{device?.os} • {device?.browser}</p>
                    <p className="flex items-center">
                      <Icon name="MapPin" size={14} className="mr-1" />
                      {device?.location} • {device?.ipAddress}
                    </p>
                    <p className="flex items-center">
                      <Icon name="Clock" size={14} className="mr-1" />
                      Last active: {formatLastAccess(device?.lastAccess)}
                    </p>
                  </div>

                  {/* Device Fingerprint */}
                  <div className="mt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleFingerprint(device?.id)}
                      iconName={showFingerprints?.[device?.id] ? "ChevronUp" : "ChevronDown"}
                      iconPosition="right"
                    >
                      Device Fingerprint
                    </Button>
                    
                    {showFingerprints?.[device?.id] && (
                      <div className="mt-2 p-3 bg-muted rounded-lg">
                        <p className="text-xs font-mono text-muted-foreground break-all">
                          {device?.fingerprint}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 ml-4">
                {!device?.trusted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTrustDevice(device?.id)}
                    iconName="Shield"
                    iconPosition="left"
                  >
                    Trust
                  </Button>
                )}
                
                {!device?.isCurrent && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRevokeDevice(device?.id)}
                    iconName="X"
                    iconPosition="left"
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Security Notice */}
      <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-1">Device Security</h4>
            <p className="text-sm text-muted-foreground">
              If you notice any unfamiliar devices or suspicious activity, revoke access immediately and change your password.
              Trusted devices have enhanced security features and reduced authentication prompts.
            </p>
          </div>
        </div>
      </div>
      {/* Remote Wipe Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Trash2" size={20} className="text-error" />
          <h3 className="text-lg font-semibold text-foreground">Emergency Actions</h3>
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            If your device is lost or compromised, you can remotely wipe all SecureVault data from it.
          </p>
          
          <Button
            variant="destructive"
            iconName="AlertTriangle"
            iconPosition="left"
            onClick={() => console.log('Initiating remote wipe...')}
          >
            Remote Wipe All Devices
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DevicesTab;